import { FitmentRowResult, IFitmentCheck, IFitmentRow, IInboxVehicle } from "../models/messagesInbox/IInboxConversation";

/**
 * Engines and gearboxes are matched on the donor's engine or gearbox code, not a part number
 * (Adam, 27 Oct 2026). Engine mounts, covers, ECUs and the like still go by part number.
 */
export interface IPartKeyCode {
  /** The fitment row that decides the match, e.g. "Engine code". */
  field: "Engine code" | "Gearbox code";
  value?: string;
}

const NOT_THE_UNIT = /mount|cover|bracket|ecu|control|module|wiring|loom|harness|sensor|sump|oil|cool|fan|bay|lever|cable|selector|guard|tray|shield/i;

export function partKeyCode(categoryName: string | undefined, donor: IInboxVehicle | undefined): IPartKeyCode | undefined {
  const name = categoryName ?? "";
  if (!name || NOT_THE_UNIT.test(name)) return undefined;
  if (/\bengines?\b/i.test(name)) return { field: "Engine code", value: donor?.engineCode };
  if (/\b(gearbox(es)?|transmissions?)\b/i.test(name)) return { field: "Gearbox code", value: donor?.gearboxCode };
  return undefined;
}

/**
 * Overall answer from the compare rows. With a key code, that row decides it: a different code
 * doesn't fit, the same code fits (or "check" if anything else differs). Without one, any
 * difference means it doesn't fit.
 */
export function fitmentOverall(rows: IFitmentRow[], keyField?: IPartKeyCode["field"]): IFitmentCheck["overall"] {
  const has = (result: FitmentRowResult, list: IFitmentRow[]) => list.some((r) => r.result === result);
  const keyRow = keyField ? rows.find((r) => r.field === keyField) : undefined;

  if (keyRow) {
    if (keyRow.result === "mismatch") return "no_fit";
    const others = rows.filter((r) => r !== keyRow);
    return keyRow.result === "check" || has("mismatch", others) || has("check", others) ? "check" : "fits";
  }
  if (has("mismatch", rows)) return "no_fit";
  return has("check", rows) ? "check" : "fits";
}
